import React, { useState, useMemo } from 'react';
import LeadEntry from './LeadEntry';
import IncompletoEntry from './IncompletoEntry';
import {
  getCoachKey, esLeadVivo, tuvoLlamada, cerroContrato, seguimientoPendiente, formatContactarEl,
  agruparPorPersona,
} from '../../utils/adminLeadsHelpers';

const AdminLeadsViewer = ({
  data, rol, incompletos = [], onReload, onLogout, reloading,
  onToggleContactado, onSetResultado, onContactarIncompleto,
}) => {
  const [tab, setTab] = useState('cal'); // 'cal' | 'no' | 'inc'
  const [coachFilter, setCoachFilter] = useState('all'); // 'all' | 'head' | 'team'
  const [search, setSearch] = useState('');
  const [soloSinContactar, setSoloSinContactar] = useState(false);
  // Vivos, post llamada y contrataron son tres vistas del MISMO eje (en qué
  // terminó el lead), así que van en un solo estado y se excluyen entre sí.
  // Con un booleano por filtro, combinar "Vivos" con "Contrataron" daba
  // siempre cero y parecía un bug. "Sin contactar" es otro eje y sí se combina.
  const [estado, setEstado] = useState('todos'); // 'todos' | 'vivos' | 'postLlamada' | 'contrataron'
  const alternarEstado = (valor) => setEstado((actual) => (actual === valor ? 'todos' : valor));

  // El mismo criterio de "hoy" que usa el correo del bot: hora de Chile, no la
  // del navegador de quien mira el panel.
  const hoy = useMemo(() => new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Santiago', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date()), []);

  // Una entrada por PERSONA, no por envío del formulario: quien lo llenó dos
  // veces tenía dos cards y contaba dos veces en todos los contadores. Se
  // agrupa acá, al mostrar, y nunca fusionando filas en la planilla.
  const calificados = useMemo(
    () => agruparPorPersona(data.calificados || []),
    [data.calificados],
  );
  const noCalificados = useMemo(
    () => agruparPorPersona(data.noCalificados || []),
    [data.noCalificados],
  );
  const botDegradado = data.botDegradado || null;
  const agendaCaida = Boolean(botDegradado?.agenda);
  const mensajesCaida = Boolean(botDegradado?.mensajes);
  const historialCaido = Boolean(botDegradado?.historial);

  // Una línea por pestaña que no se pudo leer, cada una diciendo qué se ve mal
  // por eso. Antes era un ternario anidado sobre dos pestañas; con tres, el
  // texto combinado se vuelve ilegible y la lista dice exactamente lo mismo.
  const pestanasCaidas = [
    agendaCaida && 'No se pudo leer "Agenda Llamadas": un lead calificado reciente puede aparecer sin ficha de bot sin que eso signifique que el bot lo dejó afuera.',
    mensajesCaida && 'No se pudo leer "Mensajes Bot": las conversaciones no se pueden mostrar por ahora, aunque existan.',
    historialCaido && 'No se pudo leer "Historial Resultados": la ficha no puede mostrar los cambios de resultado, y que aparezca vacía no significa que no los haya.',
  ].filter(Boolean);

  const calCountHead = useMemo(
    () => calificados.filter((l) => getCoachKey(l) === 'head').length,
    [calificados],
  );
  const calCountTeam = useMemo(
    () => calificados.filter((l) => getCoachKey(l) === 'team').length,
    [calificados],
  );

  const baseList = useMemo(() => {
    const source = tab === 'cal' ? calificados : noCalificados;
    let list = tab === 'cal' && coachFilter !== 'all'
      ? source.filter((l) => getCoachKey(l) === coachFilter)
      : source;
    return [...list].sort((a, b) => {
      if (!a.fechaIso && !b.fechaIso) return 0;
      if (!a.fechaIso) return 1;
      if (!b.fechaIso) return -1;
      return new Date(b.fechaIso) - new Date(a.fechaIso);
    });
  }, [tab, calificados, noCalificados, coachFilter]);

  const sinContactarCount = useMemo(
    () => baseList.filter((l) => !l.contactado).length,
    [baseList],
  );

  // Vivos = los que siguen dentro del funnel. No show y no cerró SÍ cuentan
  // como vivos: siguen siendo perseguibles (ver esLeadVivo).
  const vivosCount = useMemo(() => baseList.filter(esLeadVivo).length, [baseList]);

  const postLlamadaCount = useMemo(
    () => baseList.filter((l) => tuvoLlamada(l, hoy) && esLeadVivo(l)).length,
    [baseList, hoy],
  );

  const contrataronCount = useMemo(() => baseList.filter(cerroContrato).length, [baseList]);

  // Sobre TODOS los calificados, no sobre baseList: este bloque no puede
  // depender del filtro de coach ni de la búsqueda que Agustín tenga puesta.
  const porContactar = useMemo(
    () => calificados
      .filter((l) => seguimientoPendiente(l, hoy))
      .sort((a, b) => String(a.bot.contactarEl).localeCompare(String(b.bot.contactarEl))),
    [calificados, hoy],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = baseList;
    if (soloSinContactar) list = list.filter((l) => !l.contactado);
    if (estado === 'vivos') list = list.filter(esLeadVivo);
    // Post llamada = ya tuvo la reunión y todavía se puede perseguir. Sin el
    // esLeadVivo entrarían los que contrataron y los que dijeron que no, que
    // es justo lo que ensucia la lista.
    if (estado === 'postLlamada') list = list.filter((l) => tuvoLlamada(l, hoy) && esLeadVivo(l));
    if (estado === 'contrataron') list = list.filter(cerroContrato);
    if (q) {
      list = list.filter((l) =>
        [l.nombre, l.email, l.whatsapp, l.instagram].some(
          (f) => f && String(f).toLowerCase().includes(q),
        ),
      );
    }
    return list;
  }, [baseList, soloSinContactar, estado, hoy, search]);

  return (
    <div className="au-admin au-admin-shell">
      <div className="au-admin-header">
        <div className="brand">
          AU &gt;&gt; <span className="subtitle">ADMIN / LEADS</span>
        </div>
        <div className="actions">
          <button onClick={onReload} disabled={reloading}>
            {reloading ? 'Recargando…' : 'Recargar'}
          </button>
          <button onClick={onLogout}>Salir</button>
        </div>
      </div>

      <div className="au-admin-tabs">
        <button
          className={tab === 'cal' ? 'active' : ''}
          onClick={() => setTab('cal')}
        >
          Calificados <span className="count">[{String(calificados.length).padStart(3, '0')}]</span>
        </button>
        <button
          className={tab === 'no' ? 'active' : ''}
          onClick={() => setTab('no')}
        >
          No Calificados <span className="count">[{String(noCalificados.length).padStart(3, '0')}]</span>
        </button>
        {rol === 'admin' && (
          <button
            className={tab === 'inc' ? 'active' : ''}
            onClick={() => setTab('inc')}
          >
            Incompletos <span className="count">[{String(incompletos.length).padStart(3, '0')}]</span>
          </button>
        )}
      </div>

      {/* Gate doble a propósito: el botón de la pestaña ya exige rol admin,
          pero `tab` es estado local que no se remonta cuando cambia el rol
          (ej. al recargar con otra sesión), así que el contenido tiene que
          exigir el rol de nuevo acá — el botón que nunca se pisa no es
          control de acceso, es suerte. */}
      {tab === 'inc' && rol === 'admin' && (
        <>
          {incompletos.length === 0 && (
            <div className="au-week-empty">&gt;&gt; Nadie quedó a medias por ahora.</div>
          )}
          {incompletos.map((incompleto, i) => (
            <IncompletoEntry
              key={`${incompleto.email}-${i}`}
              incompleto={incompleto}
              onContactado={onContactarIncompleto}
            />
          ))}
        </>
      )}

      {tab !== 'inc' && (
        <>
          {tab === 'cal' && (
            <div className="au-admin-coach-filter">
              <span className="label">Coach:</span>
              <button
                className={coachFilter === 'all' ? 'active' : ''}
                onClick={() => setCoachFilter('all')}
              >
                Todos [{String(calificados.length).padStart(3, '0')}]
              </button>
              <button
                className={coachFilter === 'head' ? 'active' : ''}
                onClick={() => setCoachFilter('head')}
              >
                Head [{String(calCountHead).padStart(3, '0')}]
              </button>
              <button
                className={coachFilter === 'team' ? 'active' : ''}
                onClick={() => setCoachFilter('team')}
              >
                Team [{String(calCountTeam).padStart(3, '0')}]
              </button>
            </div>
          )}

          <div className="au-admin-search-bar">
            <input
              className="au-admin-search-input"
              type="text"
              placeholder="Buscar por nombre, email, WhatsApp, IG…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button
              className={`au-admin-filter-btn${soloSinContactar ? ' active' : ''}`}
              onClick={() => setSoloSinContactar((v) => !v)}
            >
              Sin contactar [{String(sinContactarCount).padStart(3, '0')}]
            </button>
            <button
              className={`au-admin-filter-btn${estado === 'vivos' ? ' active' : ''}`}
              onClick={() => alternarEstado('vivos')}
            >
              Vivos [{String(vivosCount).padStart(3, '0')}]
            </button>
            <button
              className={`au-admin-filter-btn${estado === 'postLlamada' ? ' active' : ''}`}
              onClick={() => alternarEstado('postLlamada')}
            >
              Post llamada [{String(postLlamadaCount).padStart(3, '0')}]
            </button>
            <button
              className={`au-admin-filter-btn${estado === 'contrataron' ? ' active' : ''}`}
              onClick={() => alternarEstado('contrataron')}
            >
              Contrataron [{String(contrataronCount).padStart(3, '0')}]
            </button>
            <span className="au-admin-result-count">
              {String(filtered.length).padStart(3, '0')} resultado{filtered.length !== 1 ? 's' : ''}
            </span>
          </div>

          {pestanasCaidas.length > 0 && (
            <div className="au-admin-alerta">
              {pestanasCaidas.map((texto) => (
                <div key={texto}>&gt;&gt; {texto}</div>
              ))}
              <div>
                &gt;&gt; No es que el bot haya fallado, es que ahora mismo no se puede leer el
                Sheet. Recarga en unos minutos.
              </div>
            </div>
          )}

          {porContactar.length > 0 && (
            <div className="au-admin-por-contactar">
              <div className="au-admin-por-contactar-titulo">
                &gt;&gt; CONTACTAR HOY [{String(porContactar.length).padStart(3, '0')}]
              </div>
              {porContactar.map((l) => (
                <div className="au-admin-por-contactar-item" key={`contactar-${l.email}`}>
                  <span className="nombre">{l.nombre || l.email}</span>
                  <span className="coach">{l.bot.coach}</span>
                  <span className="cuando">{formatContactarEl(l.bot.contactarEl, hoy)}</span>
                </div>
              ))}
            </div>
          )}

          {filtered.length === 0 && (
            <div className="au-week-empty">&gt;&gt; Sin entradas con este filtro.</div>
          )}

          {filtered.map((lead, i) => (
            <LeadEntry
              key={`${lead.email}-${lead.fecha}-${i}`}
              lead={lead}
              index={i}
              rol={rol}
              onToggleContactado={onToggleContactado}
              onSetResultado={onSetResultado}
              botDegradado={botDegradado}
              hoy={hoy}
            />
          ))}
        </>
      )}
    </div>
  );
};

export default AdminLeadsViewer;
